import React, { useState, useEffect } from "react"
import axios from "axios"
import { useSelector } from "react-redux"
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined"
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined"
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined"
import { DistributedIcon } from "./custom-icons"
import config from "config/config"

const PLATFORMS = [
  { name: "Distributed", icon: DistributedIcon },
  { name: "Opened", icon: VisibilityOutlinedIcon },
  { name: "Responded", icon: DoneAllOutlinedIcon },
]

export default function SurveyDashboard({
  title = "Survey Stats",
  viewMode = "month",
  fromDate = null,
  toDate = null,
}) {
  const mode = useSelector(state => state.Layout.layoutMode)
  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const userData = useSelector(state => state.Login.userData)
  const { organizations, isConsolidatedView } = useSelector(
    state => state.organizationReducer,
  )
  const [showDropdown, setShowDropdown] = useState(false)
  const [allCampaignsData, setAllCampaignsData] = useState([])
  const [distributions, setDistributions] = useState([])
  const [distributedCount, setDistributedCount] = useState(0)
  const [openedCount, setOpenedCount] = useState(0)
  const [respondedCount, setRespondedCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [timePeriod, setTimePeriod] = useState(viewMode)
  const [selectedPlatforms, setSelectedPlatforms] = useState(
    PLATFORMS.map(p => p.name),
  )

  const togglePlatform = name => {
    setSelectedPlatforms(prev =>
      prev.includes(name) ? prev.filter(p => p !== name) : [...prev, name],
    )
  }

  // Organize data by time period or custom date range
  const organizeByTimePeriod = (
    campaigns,
    timePeriod = "all",
    customFrom = null,
    customTo = null,
  ) => {
    const now = new Date()
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000)
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)

    const isWithinPeriod = date => {
      if (!date) return false
      const checkDate = new Date(date)

      // If custom dates are provided, use them
      if (customFrom && customTo) {
        const fromDateTime = new Date(customFrom)
        const toDateTime = new Date(customTo)
        // Set toDate to end of day
        toDateTime.setHours(23, 59, 59, 999)
        return checkDate >= fromDateTime && checkDate <= toDateTime
      }

      // Otherwise use preset time periods
      switch (timePeriod) {
        case "day":
          return checkDate >= oneDayAgo
        case "week":
          return checkDate >= oneWeekAgo
        case "month":
          return checkDate >= oneMonthAgo
        case "all":
        default:
          return true
      }
    }

    let totalDistributed = 0
    let totalOpened = 0
    let totalResponded = 0
    let allAssignments = []

    campaigns.forEach(campaign => {
      const distributions = campaign.SurveyDistributions || []

      distributions.forEach(distribution => {
        const assignments = distribution.SurveyCustomerAssignments || []

        assignments.forEach(assignment => {
          const createdDate = assignment.createdAt
          const openedDate = assignment.openedDate
          const respondedDate = assignment.respondedDate

          // Check if assignment was distributed in the time period
          if (isWithinPeriod(createdDate)) {
            totalDistributed++
            allAssignments.push(assignment)
          }

          // Check if assignment was opened in the time period
          // Only count if opened flag is true AND the date is within period
          if (assignment.Opened || openedDate) {
            if (isWithinPeriod(openedDate || createdDate)) {
              totalOpened++
            }
          }

          // Check if assignment was responded in the time period
          // Only count if responded flag is true AND the date is within period
          if (assignment.Responded || respondedDate) {
            if (isWithinPeriod(respondedDate || createdDate)) {
              totalResponded++
            }
          }
        })
      })
    })

    return {
      allAssignments,
      distributedCount: totalDistributed,
      openedCount: totalOpened,
      respondedCount: totalResponded,
      stats: {
        openRate:
          totalDistributed > 0
            ? ((totalOpened / totalDistributed) * 100).toFixed(2)
            : 0,
        responseRate:
          totalDistributed > 0
            ? ((totalResponded / totalDistributed) * 100).toFixed(2)
            : 0,
      },
    }
  }

  // Fetch all campaigns data once
  const fetchSurveyStats = async () => {
    try {
      setIsLoading(true)
      const orgIds = isConsolidatedView
        ? organizations.map(org => org.Organization.id).join(",")
        : activeOrganization.id
      const response = await axios.get(
        `${config.backendUrl}/survey/getDistribution/?orgIds=${orgIds}`,
        { headers: { Authorization: `Bearer ${userData.token}` } },
      )

      const campaigns = response.data.campaigns || []
      setAllCampaignsData(campaigns)

      // Initial organization with current time period or custom dates
      const organized = organizeByTimePeriod(
        campaigns,
        timePeriod,
        fromDate,
        toDate,
      )
      setDistributions(organized.allAssignments)
      setDistributedCount(organized.distributedCount)
      setOpenedCount(organized.openedCount)
      setRespondedCount(organized.respondedCount)

      setIsLoading(false)
    } catch (error) {
      console.error("Error fetching survey stats:", error)
      setIsLoading(false)
    }
  }

  // Re-organize data when time period or custom dates change
  useEffect(() => {
    if (allCampaignsData.length > 0) {
      const organized = organizeByTimePeriod(
        allCampaignsData,
        timePeriod,
        fromDate,
        toDate,
      )
      setDistributions(organized.allAssignments)
      setDistributedCount(organized.distributedCount)
      setOpenedCount(organized.openedCount)
      setRespondedCount(organized.respondedCount)
    }
  }, [timePeriod, fromDate, toDate, allCampaignsData])

  // Fetch data on mount or when organization changes
  useEffect(() => {
    if (activeOrganization?.id) {
      fetchSurveyStats()
    }
  }, [activeOrganization?.id, isConsolidatedView, organizations])

  const platforms = [
    { name: "Distributed", icon: DistributedIcon, count: distributedCount },
    { name: "Opened", icon: VisibilityOutlinedIcon, count: openedCount },
    { name: "Responded", icon: DoneAllOutlinedIcon, count: respondedCount },
  ]

  const displayedPlatforms =
    selectedPlatforms.length > 0
      ? platforms.filter(p => selectedPlatforms.includes(p.name))
      : platforms

  const maxCount = Math.max(...displayedPlatforms.map(p => p.count), 1)

  // Check if custom dates are being used
  const isCustomDateRange = fromDate && toDate

  return (
    <div
      className="br"
      style={{
        maxWidth: "768px",
        margin: "0 auto",
        padding: "24px",
        backgroundColor: mode === "dark" ? "#252F40" : "white",
        fontFamily: "Inter, sans-serif",
        boxShadow: "0 10px 15px rgba(0,0,0,0.1), 0 4px 6px rgba(0,0,0,0.05)",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "32px",
        }}
      >
        <h2
          style={{
            fontSize: "1.25rem",
            fontWeight: 600,
            color: mode === "dark" ? "white" : "#1F2937",
          }}
        >
          {title}
        </h2>

        <div
          style={{
            display: "flex",
            gap: "8px",
            backgroundColor: mode === "dark" ? "#F3F4F6" : "#F3F4F6",
            borderRadius: "8px",
            padding: "6px",
            alignItems: "center",
            position: "relative",
          }}
        >
          {/* Only show time period buttons if no custom dates */}
          {!isConsolidatedView && !isCustomDateRange && (
            <>
              {["day", "week", "month"].map(period => (
                <button
                  key={period}
                  onClick={() => setTimePeriod(period)}
                  style={{
                    padding: "6px 16px",
                    borderRadius: "6px",
                    fontSize: "0.875rem",
                    fontWeight: timePeriod === period ? 600 : 500,
                    border: "none",
                    backgroundColor:
                      timePeriod === period
                        ? mode === "dark"
                          ? "#374151"
                          : "#fff"
                        : "transparent",
                    color:
                      timePeriod === period
                        ? mode === "dark"
                          ? "#fff"
                          : "#111827"
                        : mode === "dark"
                          ? "#9CA3AF"
                          : "#4B5563",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                </button>
              ))}
            </>
          )}

          {/* Show custom date range label if dates are provided */}
          {isCustomDateRange && (
            <div
              style={{
                padding: "6px 16px",
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              {new Date(fromDate).toLocaleDateString()} -{" "}
              {new Date(toDate).toLocaleDateString()}
            </div>
          )}

          {/* Filter Dropdown Button */}
          <div style={{ position: "relative" }}>
            <button
              style={{
                padding: "6px 10px",
                borderRadius: 6,
                border: "none",
                background: "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: mode === "dark" ? "#9CA3AF" : "#6B6B6C",
                cursor: "pointer",
              }}
              onClick={() => setShowDropdown(prev => !prev)}
            >
              <FilterAltOutlinedIcon />
            </button>

            {/* Dropdown */}
            {showDropdown && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  background: mode === "dark" ? "#1F2937" : "#fff",
                  boxShadow:
                    mode === "dark"
                      ? "0 4px 12px rgba(0,0,0,0.5)"
                      : "0 4px 12px rgba(0,0,0,0.1)",
                  borderRadius: 8,
                  padding: "8px",
                  zIndex: 20,
                  minWidth: "160px",
                  border:
                    mode === "dark" ? "1px solid #374151" : "1px solid #E5E7EB",
                }}
              >
                {PLATFORMS.map(platform => (
                  <label
                    key={platform.name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      padding: "8px",
                      fontSize: "0.875rem",
                      color: mode === "dark" ? "#D1D5DB" : "#111827",
                      borderRadius: "4px",
                      transition: "background 0.2s",
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor =
                        mode === "dark" ? "#374151" : "#F3F4F6"
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = "transparent"
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedPlatforms.includes(platform.name)}
                      onChange={() => togglePlatform(platform.name)}
                      style={{ cursor: "pointer" }}
                    />
                    {platform.name}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Platform Counts */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-around",
          marginBottom: "32px",
          gap: "16px",
        }}
      >
        {displayedPlatforms.map((p, i) => {
          const Icon = p.icon
          return (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "12px",
                padding: "16px",
                borderRadius: "8px",
                minHeight: "80px",
                flex: 1,
                boxShadow:
                  mode === "dark"
                    ? "0px 5px 14px 0px rgba(0,0,0,0.5)"
                    : "0px 5px 14px 0px #E5E7EB",
              }}
            >
              {Icon && (
                <Icon
                  style={{
                    color: mode === "dark" ? "#60A5FA" : "#2990DF",
                    fontSize: 32,
                  }}
                />
              )}
              <div>
                <div
                  style={{
                    fontSize: "1.875rem",
                    fontWeight: "bold",
                    color: mode === "dark" ? "#fff" : "#1F2937",
                  }}
                >
                  {isLoading ? "…" : p.count}
                </div>
                <div
                  style={{
                    fontSize: "0.875rem",
                    color: mode === "dark" ? "#94A3B8" : "#6B7280",
                  }}
                >
                  {p.name}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Chart */}
      <div
        style={{
          position: "relative",
          height: "16rem",
          paddingTop: "16px",
        }}
      >
        {/* Y-axis */}
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: "32px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            fontSize: "0.75rem",
            color: mode === "dark" ? "#9CA3AF" : "#6B7280",
            width: "32px",
          }}
        >
          {[5, 4, 3, 2, 1, 0].map(step => (
            <span key={step}>{Math.round((maxCount / 5) * step)}</span>
          ))}
        </div>

        {/* Bars Container */}
        <div
          style={{
            marginLeft: "40px",
            height: "100%",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-around",
            gap: "48px",
            borderBottom: `1px solid ${mode === "dark" ? "#374151" : "#D1D5DB"}`,
            borderLeft: `1px solid ${mode === "dark" ? "#374151" : "#D1D5DB"}`,
          }}
        >
          {displayedPlatforms.map((p, i) => {
            const barHeight = maxCount > 0 ? (p.count / maxCount) * 100 : 0
            return (
              <div
                key={i}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  flex: 1,
                }}
              >
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-end",
                    height: "14rem",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      maxWidth: "6rem",
                      backgroundColor: mode === "dark" ? "#3B82F6" : "#2990DF",
                      borderTopLeftRadius: "4px",
                      borderTopRightRadius: "4px",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      height: `${barHeight}%`,
                      minHeight: p.count > 0 ? "4px" : "0",
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.opacity = "0.8"
                      e.currentTarget.style.transform = "scaleY(1.02)"
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.opacity = "1"
                      e.currentTarget.style.transform = "scaleY(1)"
                    }}
                  />
                </div>
                <div
                  style={{
                    fontSize: "0.875rem",
                    color: mode === "dark" ? "#D1D5DB" : "#374151",
                    marginTop: "12px",
                    fontWeight: 500,
                  }}
                >
                  {p.name}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
